import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Grid,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link as RouterLink } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  getDoctorProfile,
  listSpecializations,
  updateDoctorProfile,
} from "@/api/userApi";
import { PageHeader } from "@/components/common/PageHeader";
import type { DoctorDetail, Specialization } from "@/types/domain";

const QUALIFICATION_OPTIONS = [
  "MBBS",
  "BDS",
  "BAMS",
  "BHMS",
  "BUMS",
  "MD",
  "MS",
  "DNB",
  "DM",
  "MCh",
  "MDS",
  "MPH",
  "MBBS, MD",
  "MBBS, MS",
  "BDS, MDS",
  "Other",
] as const;

export default function DoctorProfilePage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["doctor-profile"],
    queryFn: getDoctorProfile,
  });

  const { data: specList } = useQuery({
    queryKey: ["specializations"],
    queryFn: listSpecializations,
  });

  const [form, setForm] = useState<
    Partial<DoctorDetail> & {
      qualificationDropdown?: string;
      qualificationCustom?: string;
      specializationNames?: string[];
    }
  >({});
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!data) return;
    const qual = data.qualification ?? "";
    const isPreset = QUALIFICATION_OPTIONS.some(
      (o) => o !== "Other" && o === qual,
    );
    setForm({
      ...data,
      qualificationDropdown: isPreset ? qual : qual ? "Other" : "",
      qualificationCustom: isPreset ? "" : qual,
      specializationNames:
        (data as DoctorDetail & { specializations?: string[] })
          .specializations ?? [],
    });
  }, [data]);

  const resolvedQualification =
    form.qualificationDropdown === "Other"
      ? form.qualificationCustom
      : form.qualificationDropdown;

  const save = useMutation({
    mutationFn: () =>
      updateDoctorProfile({
        fullName: form.fullName,
        qualification: resolvedQualification,
        experienceYears: form.experienceYears,
        consultationFee: form.consultationFee,
        bio: form.bio,
        clinicAddress: form.clinicAddress,
        languages: form.languages,
        specializationNames: form.specializationNames,
      } as Partial<DoctorDetail> & { specializationNames?: string[] }),
    onSuccess: (d) => {
      qc.setQueryData(["doctor-profile"], d);
      setSaved(true);
    },
  });

  const set = <K extends keyof typeof form>(
    k: K,
    v: (typeof form)[K],
  ) => setForm((f) => ({ ...f, [k]: v }));

  const specOptions: Specialization[] = specList ?? [];

  return (
    <Box>
      <PageHeader
        title="Doctor profile"
        subtitle="What patients see when they browse the directory."
      />
      <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Full name"
              value={form.fullName ?? ""}
              onChange={(e) => set("fullName", e.target.value)}
              disabled={isLoading}
            />
          </Grid>

          {/* Qualification dropdown */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              select
              label="Qualification"
              value={form.qualificationDropdown ?? ""}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  qualificationDropdown: e.target.value,
                  qualificationCustom:
                    e.target.value === "Other" ? f.qualificationCustom : "",
                }))
              }
            >
              {QUALIFICATION_OPTIONS.map((q) => (
                <MenuItem key={q} value={q}>
                  {q}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          {form.qualificationDropdown === "Other" && (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Enter your qualification"
                value={form.qualificationCustom ?? ""}
                onChange={(e) => set("qualificationCustom", e.target.value)}
                placeholder="e.g. MBBS, DNB (Cardiology)"
              />
            </Grid>
          )}

          {/* Specialization multi-select */}
          <Grid
            item
            xs={12}
            sm={form.qualificationDropdown === "Other" ? 6 : 12}
          >
            <Autocomplete
              multiple
              options={specOptions.map((s) => s.name)}
              value={form.specializationNames ?? []}
              onChange={(_, v) => set("specializationNames", v)}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const { key, ...rest } = getTagProps({ index });
                  return (
                    <Chip key={key} label={option} size="small" {...rest} />
                  );
                })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Specializations"
                  placeholder="Select specializations"
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Years of experience"
              value={form.experienceYears ?? ""}
              onChange={(e) =>
                set(
                  "experienceYears",
                  e.target.value ? Number(e.target.value) : undefined,
                )
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Consultation fee (₹)"
              value={form.consultationFee ?? ""}
              onChange={(e) =>
                set(
                  "consultationFee",
                  e.target.value ? Number(e.target.value) : undefined,
                )
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="City"
              value={form.city ?? ""}
              onChange={(e) => set("city", e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Languages (comma separated)"
              value={(form.languages ?? []).join(", ")}
              onChange={(e) =>
                set(
                  "languages",
                  e.target.value
                    .split(",")
                    .map((s) => s.trim())
                    .filter(Boolean),
                )
              }
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Clinic address"
              value={form.clinicAddress ?? ""}
              onChange={(e) => set("clinicAddress", e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Bio"
              multiline
              rows={4}
              value={form.bio ?? ""}
              onChange={(e) => set("bio", e.target.value)}
            />
          </Grid>
        </Grid>

        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
          <Button
            variant="contained"
            disabled={save.isPending || isLoading}
            onClick={() => save.mutate()}
          >
            {save.isPending ? "Saving…" : "Save profile"}
          </Button>
        </Stack>
      </Paper>

      <Button component={RouterLink} to="/delete-account" color="error" sx={{ mt: 3 }}>
        Delete my account and data
      </Button>

      <Snackbar
        open={saved}
        autoHideDuration={3000}
        onClose={() => setSaved(false)}
      >
        <Alert severity="success" variant="filled">
          Profile saved
        </Alert>
      </Snackbar>
    </Box>
  );
}
